<script setup lang="ts">
  import { DefaultTable, PaginateOrScroll, SearchNoResults, TableHeaderSection } from '@/shared/ui';
  import type {
    MemberTrainingStatus,
    TrainingCollaboratorListItem,
    TrainingCollaboratorListMetadata,
  } from '@/entities/Training';
  import {
    COLLABORATOR_ORDER_OPTIONS,
    COLLABORATOR_TABLE_COLUMN_WIDTH_CLASSES,
    COLLABORATOR_TABLE_HEADERS,
    collaboratorTableCellModifierClass,
  } from '../../model/useTrainingCollaboratorsTable';
  import TrainingCollaboratorsTableRow from './TrainingCollaboratorsTableRow.vue';

  withDefaults(
    defineProps<{
      collaborators: TrainingCollaboratorListItem[];
      allCollaborators?: TrainingCollaboratorListItem[];
      metadata: TrainingCollaboratorListMetadata | null;
      itemsPerPage: number;
      currentSort: string;
      currentOrder: string;
      loadingMore?: boolean;
      hasMoreItems?: boolean;
      isFilterActive?: boolean;
      isChangingStatus?: (id: number) => boolean;
      generatingIds?: number[];
    }>(),
    { isChangingStatus: () => false, generatingIds: () => [] }
  );

  const emit = defineEmits<{
    'change-page': [page: number];
    'update:sort': [sort: string, order: string];
    'upload-certificate': [collaborator: TrainingCollaboratorListItem];
    'view-certificate': [collaborator: TrainingCollaboratorListItem];
    'generate-certificate': [collaborator: TrainingCollaboratorListItem];
    'filter-clicked': [];
    'change-status': [id: number, status: MemberTrainingStatus];
  }>();

  function collaboratorThClass(_header: string, index: number) {
    return [
      'text-left text-xs font-semibold text-gray-07 pl-6 py-4.5 align-middle',
      collaboratorTableCellModifierClass(index),
    ]
      .filter(Boolean)
      .join(' ');
  }
</script>

<template>
  <PaginateOrScroll
    data-testid="training-collaborators-paginate"
    :items="collaborators"
    :all-items="allCollaborators ?? []"
    :loading-more="loadingMore ?? false"
    :has-more-items="hasMoreItems ?? false"
    :metadata="metadata"
    :selected-quantity="itemsPerPage"
    entity-name="colaborador"
    @page-changed="(newPage: number) => emit('change-page', newPage)"
  >
    <template #table="{ items: pageItems }">
      <DefaultTable table-class="w-full table-fixed divide-y divide-gray-02">
        <colgroup>
          <col
            v-for="widthClass in COLLABORATOR_TABLE_COLUMN_WIDTH_CLASSES"
            :key="widthClass"
            :class="widthClass.split(' ')[0]"
          />
        </colgroup>
        <TableHeaderSection
          title="Lista de Colaboradores"
          :headers="COLLABORATOR_TABLE_HEADERS"
          :has-selection="false"
          :has-selected="false"
          :selected-count="0"
          :selected-documents="[]"
          :all-selected="false"
          :item-count="pageItems.length"
          item-type="colaboradores"
          :order-options="COLLABORATOR_ORDER_OPTIONS"
          :current-sort="currentSort"
          :current-order="currentOrder"
          :has-filter="true"
          :is-filter-active="isFilterActive ?? false"
          :has-delete="false"
          :show-column-headers="pageItems.length > 0"
          :th-class="collaboratorThClass"
          @update:sort="(sort: string, order: string) => emit('update:sort', sort, order)"
          @filter-clicked="emit('filter-clicked')"
        />
        <tbody class="border-t border-gray-02">
          <tr v-if="pageItems.length === 0">
            <td
              :colspan="COLLABORATOR_TABLE_HEADERS.length"
              class="py-8"
            >
              <SearchNoResults data-testid="training-collaborators-no-results-state" />
            </td>
          </tr>
          <TrainingCollaboratorsTableRow
            v-for="collaborator in pageItems"
            :key="collaborator.id"
            :collaborator="collaborator"
            :is-changing-status="isChangingStatus"
            :is-generating="generatingIds.includes(collaborator.id)"
            @change-status="(id, status) => emit('change-status', id, status)"
            @upload-certificate="emit('upload-certificate', $event)"
            @view-certificate="emit('view-certificate', $event)"
            @generate-certificate="emit('generate-certificate', $event)"
          />
        </tbody>
      </DefaultTable>
    </template>
  </PaginateOrScroll>
</template>
